import { Typography, Tag } from 'antd';
import { formatVND } from '@/utils/format';
import type { TenantOrder } from '@/mocks/orders';
import { POS_IMAGE_FALLBACK } from '../../POS/utils/imagePlaceholder';
import { groupOrderItemsByCombo } from '../utils/groupOrderItemsByCombo';
import { comboComponentQty } from '@/components/Orders/ComboComponentsList';

const { Text } = Typography;

type OrderItem = NonNullable<TenantOrder['items']>[number];

interface OrderSummaryItemsProps {
  items: OrderItem[];
}

const LINK_COLOR = '#1677ff';

function ItemThumb({ src, alt }: { src?: string; alt: string }) {
  return (
    <img
      src={src || POS_IMAGE_FALLBACK}
      alt={alt}
      onError={(event) => {
        (event.currentTarget as HTMLImageElement).src = POS_IMAGE_FALLBACK;
      }}
      style={{
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 4,
        flex: 'none',
        background: '#f5f5f5',
        border: '1px solid #f0f0f0',
      }}
    />
  );
}

/**
 * TCR-2493 — nhãn kho của một hàng sản phẩm trong pop-up xem nhanh.
 *
 * ƯU TIÊN MÃ KHO (`warehouse_code`) vì đó là thứ người dùng đối chiếu với
 * danh mục kho; chỉ khi không có mã (đơn cũ, hoặc BE chưa populate) mới lùi
 * về tên kho. Không có cả hai thì hiện "---" thay vì bỏ trống — dòng "Kho:"
 * rỗng trông như lỗi hiển thị.
 *
 * Cụm combo gồm nhiều dòng hàng nên gom MÃ KHO KHÁC NHAU của các dòng thành
 * danh sách (thường chỉ một) — bỏ trùng lặp để không in ra "HN01, HN01".
 */
function resolveWarehouseLabel(members: OrderItem[]): string {
  const collect = (pick: (member: OrderItem) => string | undefined): string[] => {
    const values: string[] = [];
    for (const member of members) {
      const value = pick(member);
      if (value && !values.includes(value)) values.push(value);
    }
    return values;
  };

  const codes = collect((member) => member.warehouse_code);
  if (codes.length > 0) return codes.join(', ');

  const names = collect((member) => member.warehouse_name);
  if (names.length > 0) return names.join(', ');

  return '---';
}

/**
 * Danh sách sản phẩm của pop-up xem nhanh — trình bày dạng danh sách dọc giống pop-up đơn sàn
 * bên ECom (ảnh · mã · tên · "Số lượng — thành tiền"), KHÔNG dùng bảng như trang chi tiết:
 * bảng có bề ngang tối thiểu lớn hơn một cột của pop-up nên đẻ ra thanh cuộn ngang (TCR-1095).
 *
 * Cụm combo vẫn gom một mục kèm các sản phẩm thành phần, dùng chung `groupOrderItemsByCombo`
 * với bảng ở trang chi tiết để hai nơi gom giống nhau.
 */
export function OrderSummaryItems({ items }: OrderSummaryItemsProps) {
  const rows = groupOrderItemsByCombo(items);

  if (rows.length === 0) {
    return <Text type="secondary">Không có sản phẩm</Text>;
  }

  return (
    <div data-testid="order-summary-items" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((row) => {
        const first = row.members[0];
        const isCombo = row.kind === 'combo';
        const title = isCombo ? (row.combo_name || 'Combo') : first.product_name;
        const code = isCombo ? row.combo_sku : first.sku;
        const quantity = isCombo ? row.unit_count : first.qty;
        const amount = isCombo ? row.combo_total : first.price * first.qty;

        return (
          <div key={row.key} style={{ display: 'flex', gap: 8, minWidth: 0 }}>
            <ItemThumb src={first.image || first.image_url} alt={title || code || ''} />
            <div style={{ minWidth: 0, flex: 1 }}>
              {code && (
                <div style={{ fontSize: 12, lineHeight: 1.4, fontWeight: 600, color: LINK_COLOR, wordBreak: 'break-word' }}>
                  {code}
                </div>
              )}
              <div style={{ fontSize: 12, lineHeight: 1.4, wordBreak: 'break-word' }}>
                {title || '—'}
                {/* TCR-1244: dòng bán theo giá flash sale — popup tóm tắt hiện
                    ngay sau khi ghi hoá đơn là nơi thu ngân đối chiếu lần cuối,
                    nên phải nói ra như màn chi tiết. */}
                {(first.flash_sale_item_ids ?? []).length > 0 && (
                  <Tag
                    color="red"
                    data-testid="order-summary-item-flash-tag"
                    style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, lineHeight: '16px' }}
                  >
                    Flash sale
                  </Tag>
                )}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Số lượng: {quantity} — {formatVND(amount)}
              </Text>
              {/* TCR-2493 — mã kho của dòng hàng, hiện NGAY dưới dòng số lượng
                  để người xem biết hàng này lấy từ kho nào mà không phải mở
                  màn chi tiết. Cùng cỡ chữ/cỡ chữ phụ với dòng số lượng ngay
                  trên nên không làm rố bố cục pop-up. */}
              <Text
                type="secondary"
                style={{ fontSize: 12, display: 'block' }}
                data-testid="order-summary-item-warehouse"
              >
                Kho: {resolveWarehouseLabel(row.members)}
              </Text>

              {isCombo && (
                <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #f0f0f0' }}>
                  {row.members.map((member, index) => (
                    <div key={`${member.sku}-${index}`}>
                      <div style={{ fontSize: 12, color: '#6b6b6b', wordBreak: 'break-word' }}>
                        {member.combo_unit_qty ?? 1}× {member.product_name || member.sku}
                      </div>
                      {/* Thành viên cụm ảo LẠI là combo thật: lồng thêm một
                          tầng thành phần của chính nó, cùng kiểu chữ. */}
                      {(member.bom_components ?? []).length > 0 && (
                        <div style={{ paddingLeft: 8, borderLeft: '2px solid #f0f0f0' }}>
                          {(member.bom_components ?? []).map((component, componentIndex) => (
                            <div
                              key={`${component.sku}-${componentIndex}`}
                              style={{ fontSize: 12, color: '#6b6b6b', wordBreak: 'break-word' }}
                            >
                              {comboComponentQty(component, member.qty)}× {component.product_name || component.sku}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* TCR-1517 — COMBO THẬT chiếm đúng MỘT dòng đơn (không gom cụm),
                  nên nó rơi vào nhánh `loose` và trước đây pop-up này không hiện
                  gì về ruột combo. Trình bày y hệt combo ảo ngay trên: cùng viền
                  trái, cùng kiểu chữ — pop-up không có cột nào để căn, nên KHÔNG
                  dựng bảng ở đây (bảng đẻ ra cuộn ngang, xem TCR-1095). */}
              {!isCombo && (first.bom_components ?? []).length > 0 && (
                <div
                  data-testid={`order-summary-combo-components-${first.sku}`}
                  style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #f0f0f0' }}
                >
                  {(first.bom_components ?? []).map((component, index) => (
                    <div
                      key={`${component.sku}-${index}`}
                      style={{ fontSize: 12, color: '#6b6b6b', wordBreak: 'break-word' }}
                    >
                      {comboComponentQty(component, first.qty)}× {component.product_name || component.sku}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
